import React, { useEffect, useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { ColorSquare } from '@/components/ColorSquare'
import { TestDeviceIcon } from '@/components/TestDeviceIcon'
import { Toast } from '@/components/Toast'
import { PatientId } from '@/components/PatientId'
import { StudySelectorForDevice } from '@/components/StudySelectorForDevice'
import { useAuth } from '@/hooks/useAuth'
import { DeviceService, StudyService, UserProfileHelper } from '@lura/shared'
import type {
  DeviceWithAssignments,
  ClinicianDevice,
  StudyWithCounts
} from '@lura/shared'

export const DeviceListPage: React.FC = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [devices, setDevices] = useState<
    (DeviceWithAssignments | ClinicianDevice)[]
  >([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<
    'serial' | 'date-newest' | 'date-oldest'
  >('serial')

  // Create device form state
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newSerialNumber, setNewSerialNumber] = useState('')
  const [newIsTestDevice, setNewIsTestDevice] = useState(false)
  const [newStudyId, setNewStudyId] = useState<string | null>(null)
  const [availableStudies, setAvailableStudies] = useState<StudyWithCounts[]>([])
  const [creating, setCreating] = useState(false)

  // Study Admin and clinician access check
  if (!UserProfileHelper.isStudyAdmin(user) && !UserProfileHelper.isClinician(user)) {
    return <Navigate to="/studies" replace />
  }

  const isStudyAdmin = UserProfileHelper.isStudyAdmin(user)

  const fetchDevices = async () => {
    try {
      setLoading(true)
      setError(null)
      // Admins see all devices, clinicians see only devices in their assigned studies
      if (isStudyAdmin) {
        const data = await DeviceService.getAllDevices()
        setDevices(data)
      } else if (user?.profile?.id) {
        const data = await DeviceService.getClinicianDevices(user.profile.id)
        setDevices(data)
      } else {
        setDevices([])
      }
    } catch (err) {
      console.error('Error fetching devices:', err)
      setError(err instanceof Error ? err.message : 'Failed to load devices')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDevices()
  }, [])

  // Fetch available studies when create form is shown (admin only)
  useEffect(() => {
    const fetchStudies = async () => {
      if (showCreateForm && isStudyAdmin) {
        try {
          const studies = await StudyService.getAllStudies()
          setAvailableStudies(studies)
        } catch (err) {
          console.error('Error fetching studies:', err)
        }
      }
    }
    fetchStudies()
  }, [showCreateForm, isStudyAdmin])

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleCreateDevice = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newSerialNumber.trim()) {
      setErrorMessage('Serial number is required')
      return
    }

    try {
      setCreating(true)
      setErrorMessage(null)
      setSuccessMessage(null)
      await DeviceService.createDevice(
        newSerialNumber.trim(),
        newIsTestDevice,
        newStudyId || undefined
      )

      // Reset form
      setNewSerialNumber('')
      setNewIsTestDevice(false)
      setNewStudyId(null)
      setShowCreateForm(false)
      setSuccessMessage('Device created successfully')

      // Refresh devices list
      await fetchDevices()
    } catch (err) {
      console.error('Error creating device:', err)
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to create device'
      )
    } finally {
      setCreating(false)
    }
  }

  // Filter and sort devices
  const filteredAndSortedDevices = React.useMemo(() => {
    let filtered = devices

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      filtered = devices.filter(
        device =>
          device.serial_number.toLowerCase().includes(searchLower) ||
          device.study_title?.toLowerCase().includes(searchLower)
      )
    }

    // Apply sorting
    const sorted = [...filtered]
    if (sortBy === 'serial') {
      sorted.sort((a, b) => a.serial_number.localeCompare(b.serial_number))
    } else if (sortBy === 'date-newest') {
      sorted.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    } else if (sortBy === 'date-oldest') {
      sorted.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
    }

    return sorted
  }, [devices, searchQuery, sortBy])

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Devices"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Page Header */}
          <div className="flex justify-between items-center mb-8">
            <h2
              className="text-5xl font-normal text-dark-blue"
              style={{ fontFamily: 'Family' }}
            >
              Devices
            </h2>
            {isStudyAdmin && (
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-dark-blue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors"
              >
                {showCreateForm ? 'Cancel' : '+ Create Device'}
              </button>
            )}
          </div>

          {/* Search and Sort */}
          <div className="mb-6 space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search devices..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-2.5 border border-gray-300 rounded-2xl hover:bg-gray-50 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Sort Buttons */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                Sort by:
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSortBy('serial')}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    sortBy === 'serial'
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Serial Number
                </button>
                <button
                  onClick={() => setSortBy('date-newest')}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    sortBy === 'date-newest'
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Newest First
                </button>
                <button
                  onClick={() => setSortBy('date-oldest')}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    sortBy === 'date-oldest'
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Oldest First
                </button>
              </div>
            </div>
          </div>

          {/* Error State for page-level errors (loading data) */}
          {error && (
            <div className="mb-6 rounded-3xl bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Create Device Form */}
          {showCreateForm && (
            <div className="mb-6 bg-white shadow-sm rounded-3xl p-6">
              <h3 className="text-xl font-medium text-dark-blue mb-4">
                Create New Device
              </h3>
              <form onSubmit={handleCreateDevice}>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="serialNumber"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Serial Number
                    </label>
                    <input
                      type="text"
                      id="serialNumber"
                      value={newSerialNumber}
                      onChange={e => setNewSerialNumber(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
                      placeholder="Enter device serial number"
                      disabled={creating}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Study (Optional)
                    </label>
                    <StudySelectorForDevice
                      studies={availableStudies}
                      selectedStudy={availableStudies.find(s => s.id === newStudyId) || null}
                      onSelect={study => setNewStudyId(study?.id || null)}
                      placeholder="Select a study..."
                      disabled={creating}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isTestDevice"
                      checked={newIsTestDevice}
                      onChange={e => setNewIsTestDevice(e.target.checked)}
                      className="h-4 w-4 text-dark-blue focus:ring-dark-blue border-gray-300 rounded"
                      disabled={creating}
                    />
                    <label
                      htmlFor="isTestDevice"
                      className="text-sm text-gray-700"
                    >
                      Test Device
                    </label>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false)
                        setNewSerialNumber('')
                        setNewIsTestDevice(false)
                        setNewStudyId(null)
                        setError(null)
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-3xl text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors"
                      disabled={creating}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-3xl text-sm font-medium text-white bg-dark-blue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors disabled:opacity-50"
                      disabled={creating}
                    >
                      {creating ? 'Creating...' : 'Create Device'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          )}

          {/* Devices Table */}
          {!loading && !error && (
            <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  All Devices
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  All registered devices in the system
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-highlight-green">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Device
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Study
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Most Recent User
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Firmware
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Created Date
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Manage</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAndSortedDevices.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-12 text-center text-sm text-gray-500"
                        >
                          {searchQuery
                            ? 'No devices found matching your search'
                            : 'No devices in the system. Create your first device to get started.'}
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedDevices.map(device => (
                        <tr
                          key={device.id}
                          onClick={() => navigate(`/devices/${device.id}`)}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {device.serial_number}
                              </span>
                              {device.is_test_device && <TestDeviceIcon />}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {device.study_title && device.study_color ? (
                              <div className="flex items-center gap-3">
                                <ColorSquare
                                  color={device.study_color}
                                  size="md"
                                />
                                <div className="text-sm text-gray-900">
                                  {device.study_title}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">
                                Not assigned
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {device.most_recent_user_id ? (
                              <div className="text-sm">
                                <div className="text-gray-900">
                                  <PatientId
                                    patientId={device.most_recent_user_id}
                                  />
                                </div>
                                {device.last_used_at && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {new Date(
                                      device.last_used_at
                                    ).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">
                                Not yet used
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-500">
                              {(device as any).firmware_version || '—'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {new Date(device.created_at).toLocaleDateString(
                                'en-US',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex justify-end">
                              <svg
                                className="w-5 h-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Toast Notifications */}
      <Toast
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      <Toast
        message={errorMessage}
        type="error"
        onClose={() => setErrorMessage(null)}
      />
    </div>
  )
}
